import React, { useRef } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { integrations } from '../../data/mockData';

const IntegrationsSection = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="bg-white py-24" id="integrations" aria-labelledby="integrations-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
          <div>
            <span className="text-sm font-medium text-purple-600 mb-3 block">Integrations</span>
            <h2 id="integrations-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Integrate Agents with{' '}
              <br className="hidden sm:block" />
              <span className="text-gray-400">every app in your stack</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-xl">
              Extend what your agents can do with developer APIs for data, knowledge &amp; interfaces.
            </p>
          </div>
          <a
            href="#integrations"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mt-6 lg:mt-0 group"
            aria-label="View all integrations"
          >
            See all integrations
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
          </a>
        </header>

        {/* Navigation Buttons */}
        <nav className="flex gap-2 mb-6 justify-end" aria-label="Integration carousel navigation">
          <button
            onClick={() => scroll('left')}
            className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            aria-label="Scroll integrations left"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" aria-hidden="true" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            aria-label="Scroll integrations right"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" aria-hidden="true" />
          </button>
        </nav>

        {/* Scrollable Cards */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          role="list"
          aria-label="Available integrations"
        >
          {[...integrations, ...integrations].map((integration, index) => (
            <article
              key={`${integration.id}-${index}`}
              className="flex-shrink-0 w-72 snap-start group"
              role="listitem"
            >
              <div className="rounded-xl bg-white border border-gray-200 p-6 h-full hover:border-gray-300 hover:shadow-lg transition-all duration-300">
                {/* Logo */}
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4 overflow-hidden">
                  <img
                    src={integration.logo}
                    alt={`${integration.name} logo`}
                    className="w-8 h-8 object-contain"
                    loading="lazy"
                    width="32"
                    height="32"
                  />
                </div>
                
                {/* Category Tag */}
                <span className="inline-block text-xs text-purple-600 font-mono mb-3">
                  {'{' + integration.category + '}'}
                </span>
                
                {/* Title */}
                <h3 className="text-gray-900 font-medium mb-2">
                  {integration.name}
                </h3>
                
                {/* Description */}
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  {integration.description}
                </p>
                
                {/* Link */}
                <a
                  href="#integrations"
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 group/link"
                  aria-label={`Get started with ${integration.name} integration`}
                >
                  Get started
                  <ArrowRight className="ml-1 w-3 h-3 group-hover/link:translate-x-1 transition-transform" aria-hidden="true" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IntegrationsSection;
